/**
 * Email & Calendar settings section.
 *
 * Renders the Gmail integration and handles Slack/Fathom OAuth callback
 * banners. This section is the default redirect target when navigating to
 * settings/integrations, so it displays success/error banners for Slack
 * OAuth callbacks that land here via redirect.
 */

import { ArrowRight, Mail01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Key } from 'react-aria-components';
import { useSearchParams } from 'react-router-dom';

import GmailIntegration from '@/components/GmailIntegration';
import { IntegrationPrivacyNote } from '@/components/integrations/IntegrationCards';
import OutlookIntegration from '@/components/OutlookIntegration';
import { useOrg } from '@/contexts/OrgContext';
import { useConnectedAccounts } from '@/hooks/integrations/useConnectedAccounts';
import { useProfile } from '@/hooks/profile/useProfile';
import { useCanWrite } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state/empty-state';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { CARD_BORDER } from '@/lib/base/list-card';
import { AlertFullWidth } from '@/lib/base/overlays/alerts';
import {
  DEFAULT_SLACK_ERROR_MESSAGE,
  SLACK_ERROR_MESSAGES,
} from '@/lib/integrations/slack-errors';
import { useViewHealthMarker } from '@/lib/view-health';
import { cx } from '@/utils/cx';

import { useUser } from '../../../../allauth_auth';

const AUTOMATIC_OPTION_ID = 'automatic';

export function EmailCalendarSection() {
  const { currentOrg } = useOrg();
  const user = useUser();
  const {
    formData,
    membershipId,
    membershipLoadError,
    setBookingCalendarAccount,
    bookingCalendarSaving,
    bookingCalendarError,
  } = useProfile({ orgId: currentOrg?.id, userId: user?.id });
  const { accounts } = useConnectedAccounts({ orgId: currentOrg?.id });
  useViewHealthMarker('settings-section-ready');
  const [searchParams] = useSearchParams();
  // A read-only seat gets no mailbox: the backend refuses the connect
  // (viewer_read_only) and membership creation skips auto-connect, so
  // offering the Connect buttons would only lead to a 403.
  const canWrite = useCanWrite();

  const googleAccounts = useMemo(
    () => accounts.filter(a => a.provider === 'google'),
    [accounts]
  );

  const bookingCalendarOptions = useMemo(
    () => [
      { id: AUTOMATIC_OPTION_ID, label: 'Automatic' },
      ...googleAccounts.map(a => ({
        id: String(a.id),
        label: a.email || String(a.id),
      })),
    ],
    [googleAccounts]
  );

  const bookingCalendarSelectedKey = useMemo(() => {
    const stored = formData.booking_calendar_social_account_id;
    if (stored != null && googleAccounts.some(a => a.id === stored)) {
      return String(stored);
    }
    return AUTOMATIC_OPTION_ID;
  }, [formData.booking_calendar_social_account_id, googleAccounts]);

  const onBookingCalendarChange = useCallback(
    (key: Key | null) => {
      if (key === null) {
        return;
      }
      const value = key === AUTOMATIC_OPTION_ID ? null : Number(key);
      setBookingCalendarAccount(value).catch(() => {});
    },
    [setBookingCalendarAccount]
  );

  const [slackSuccess, setSlackSuccess] = useState(false);
  const [slackError, setSlackError] = useState<string | null>(null);

  useEffect(() => {
    const success = searchParams.get('slack_success');
    const error = searchParams.get('slack_error');

    if (success === 'true') {
      setSlackSuccess(true);
      setSlackError(null);
    } else {
      setSlackSuccess(false);
    }

    if (error) {
      setSlackError(error);
      setSlackSuccess(false);
      if (!SLACK_ERROR_MESSAGES[error]) {
        console.warn('Unknown Slack OAuth error code:', error);
      }
    } else {
      setSlackError(null);
    }
  }, [searchParams]);

  const dismissSlackSuccess = useCallback(() => setSlackSuccess(false), []);
  const dismissSlackError = useCallback(() => setSlackError(null), []);

  return (
    <div className='space-y-8'>
      {/* Two bounded columns, not a title row with an action floating over it.
          Previously the action shared a row with the title alone, so the
          subtitle below spanned the full width and ran *underneath* the link —
          the paragraph and the link collided in the same horizontal band, which
          is what made the link read as detached from the header.
          `min-w-0` on the text column makes the subtitle wrap before the
          action's column instead. */}
      <div className='flex items-start justify-between gap-8'>
        <div className='min-w-0'>
          <h2 className='text-lg font-semibold text-fg-primary'>
            Email &amp; calendar
          </h2>
          <p className='text-sm text-fg-tertiary mt-0.5'>
            {canWrite
              ? 'Connect an account so Ahoy can use your email and calendar for scheduling and meeting context.'
              : 'Email and calendar accounts come with a full seat.'}
          </p>
        </div>
        {/* Link-style: navigation to another page, not the task on this one. As
            a bordered button it read as a third peer to the two Connect
            buttons, which are the actual primary action here.
            mt-1 optically centres the link's 20px text box on the heading's
            28px line ((28-20)/2 = 4px); `items-start` alone left it high.
            link-gray is `p-0`, so its text is already flush with the card
            borders below — no padding compensation needed. */}
        {canWrite && currentOrg?.id && (
          <Button
            size='sm'
            color='link-gray'
            iconTrailing={ArrowRight}
            href={`/${currentOrg.id}/settings/connected-accounts`}
            className='mt-1 shrink-0'
          >
            Manage all accounts
          </Button>
        )}
      </div>

      <div className='space-y-6'>
        {/* Slack Success Banner */}
        {slackSuccess && (
          <AlertFullWidth
            color='success'
            title='Slack workspace connected successfully'
            description='Your Slack workspace is now connected to capture meeting notes.'
            onClose={dismissSlackSuccess}
          />
        )}

        {/* Slack Error Banner */}
        {slackError && (
          <AlertFullWidth
            color='error'
            title="We couldn't connect Slack"
            description={
              SLACK_ERROR_MESSAGES[slackError] ?? DEFAULT_SLACK_ERROR_MESSAGE
            }
            onClose={dismissSlackError}
          />
        )}

        {/* Default booking calendar picker — shown only when >=2 Google accounts */}
        {googleAccounts.length >= 2 && (
          <div>
            <h3 className='text-sm font-semibold text-fg-primary'>
              Default booking calendar
            </h3>
            <p className='text-sm text-fg-tertiary mt-0.5'>
              The calendar Ahoy uses for new booking links by default.
            </p>
            <div className='mt-3'>
              <Select
                size='sm'
                className='w-48'
                aria-label='Default booking calendar'
                items={bookingCalendarOptions}
                selectedKey={bookingCalendarSelectedKey}
                onSelectionChange={onBookingCalendarChange}
                isDisabled={
                  bookingCalendarSaving ||
                  !!membershipLoadError ||
                  membershipId === null
                }
              >
                {item => <SelectItem id={item.id}>{item.label}</SelectItem>}
              </Select>
            </div>
            {bookingCalendarError && (
              <AlertFullWidth
                color='error'
                title={bookingCalendarError}
                description=''
              />
            )}
          </div>
        )}

        {canWrite ? (
          <>
            {/* Each provider is one contained card. Without it the provider
                header, its CTA, and its benefit list read as three unrelated
                things floating on the page, and the two providers bleed
                together. */}
            <div className={cx(CARD_BORDER, 'p-5')}>
              <GmailIntegration />
            </div>

            <div className={cx(CARD_BORDER, 'p-5')}>
              <OutlookIntegration />
            </div>

            {/* Stated once for the whole section — it applies to every
                provider, so repeating it under each one was duplication, not
                reassurance. */}
            <IntegrationPrivacyNote />
          </>
        ) : (
          <div className={cx(CARD_BORDER, 'p-5')}>
            <EmptyState
              icon={<Mail01 className='size-5' />}
              iconColor='gray'
              size='sm'
              title='No email or calendar on a view-only seat'
              description="Nothing is synced from your own inbox or calendar. You'll see the email and meetings your team shares on each record."
            />
          </div>
        )}
      </div>
    </div>
  );
}
